// ConfirmTierModal.hook.ts owns the data layer for the Lead Agent tier
// confirmation modal (RET-1230 / C2; RET-1287; RET-2129): the products catalog,
// tier selection and renewal projection. After the plan renewal it also owns the
// immediate reactivation preview/confirm state machine, including client-side
// 3DS. Before renewal the original deferred /accept flow remains unchanged.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { SubscriptionManagerService } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import type {
  tRelatedProduct,
  tRenewalProjectionWireData,
} from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';
import { ePlanFrequency } from 'src/providers/SubscriptionProvider/Subscription.enums';
import type {
  tLeadAgentAcceptanceOrigin,
  tLeadAgentReactivatePreview,
  tLeadAgentReactivateResult,
  tLeadAgentRenewalPhase,
  tLeadAgentSelectableTier,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { LeadAgentAcceptanceService } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.service';
import { extractPaymentIntentData } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import { stripePromise } from 'src/constants/stripe.constants';
import {
  findLeadAgentProductForTier,
  readLeadAgentErrorCode,
  resolveTierProducts,
} from '../acceptanceModals.functions';
import type { tLeadAgentTierProduct } from '../acceptanceModals.functions';

export type tConfirmTierModalCatalog =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      /** Tiers with a resolvable SKU for the workspace freq + currency. */
      tierProducts: tLeadAgentTierProduct[];
    };

export type tConfirmTierModalProjection =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: tRenewalProjectionWireData };

export type tConfirmTierModalReactivatePreview =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: tLeadAgentReactivatePreview };

export type tConfirmTierModalChargeView =
  | { status: 'confirm' }
  | { status: 'processing' }
  | { status: 'error' }
  | { status: 'success'; result: tLeadAgentReactivateResult }
  | {
      status: 'pending';
      kind: 'invoice' | 'open_invoice' | 'webhook';
      result: tLeadAgentReactivateResult;
    };

export type tUseConfirmTierModalArgs = {
  workspaceId: string;
  suggestedTier: tLeadAgentSelectableTier;
  frequency: ePlanFrequency;
  currency: string;
  /** Personal first-charge date from the acceptance; omitted from the query when null. */
  nextPaymentAt: string | null;
  /**
   * Acceptance cohort (RET-1290). Forwarded to the projection so the breakdown
   * reflects the bridge charge for presale non-monthly plans (vs full at
   * renewal for monthly / post-launch). Omitted from the query when null.
   */
  origin: tLeadAgentAcceptanceOrigin | null;
  /** Backend-owned renewal phase. Only after_renewal charges immediately. */
  phase?: tLeadAgentRenewalPhase | null;
};

export type tUseConfirmTierModal = {
  catalog: tConfirmTierModalCatalog;
  projection: tConfirmTierModalProjection;
  selectedTier: tLeadAgentSelectableTier;
  selectedProduct: tRelatedProduct | null;
  isChargeNow: boolean;
  reactivationPreview: tConfirmTierModalReactivatePreview;
  chargeView: tConfirmTierModalChargeView;
  selectTier: (tier: tLeadAgentSelectableTier) => void;
  /** Switches a stale /accept response onto the reactivation money path. */
  startChargeNow: () => void;
  /** Returns the backend charge result; null means no terminal outcome. */
  confirmChargeNow: () => Promise<tLeadAgentReactivateResult | null>;
  /** A deliberate payment retry always gets a fresh preview/date/idempotency key. */
  retryChargeNow: () => void;
  /** Re-fetches the catalog (catalog error) or the projection (projection error). */
  retry: () => void;
};

function isCanceled(error: unknown): boolean {
  return (error as AxiosError | undefined)?.code === 'ERR_CANCELED';
}

/**
 * Loads the products catalog once (tier selector prices) and the renewal
 * projection on every tier change. Each projection fetch aborts the previous
 * one (AbortController) so a fast tier switch can't apply a stale breakdown.
 * The projection endpoint (RET-1287) returns the authoritative lines + total +
 * first charge; the hook never fabricates an amount — a missing SKU, a 404
 * (no billable sub) or a 422 (unsupported plan / invalid tier) all surface as
 * `status: 'error'` so the modal fails closed (confirm disabled + retry).
 */
export function useConfirmTierModal(
  args: tUseConfirmTierModalArgs,
): tUseConfirmTierModal {
  const {
    workspaceId,
    suggestedTier,
    frequency,
    currency,
    nextPaymentAt,
    origin,
    phase,
  } = args;

  const [catalog, setCatalog] = useState<tConfirmTierModalCatalog>({
    status: 'loading',
  });
  const [products, setProducts] = useState<tRelatedProduct[]>([]);
  const [selectedTier, setSelectedTier] =
    useState<tLeadAgentSelectableTier>(suggestedTier);
  const [projection, setProjection] = useState<tConfirmTierModalProjection>({
    status: 'idle',
  });
  // Bumped on retry to re-trigger the projection effect without changing tier.
  const [projectionNonce, setProjectionNonce] = useState(0);
  const [catalogNonce, setCatalogNonce] = useState(0);
  const [isChargeNow, setIsChargeNow] = useState(phase === 'after_renewal');
  const [reactivationPreview, setReactivationPreview] =
    useState<tConfirmTierModalReactivatePreview>({ status: 'idle' });
  const [chargeView, setChargeView] = useState<tConfirmTierModalChargeView>({
    status: 'confirm',
  });
  const [reactivationNonce, setReactivationNonce] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const reactivateAbortRef = useRef<AbortController | null>(null);
  const chargeInFlightRef = useRef(false);
  // Ambiguous failures replay the exact pinned date/idempotency key. Only a
  // confirmed terminal outcome may replace it with a fresh preview on retry.
  const refreshPreviewOnRetryRef = useRef(false);
  const isMountedRef = useRef(true);
  const activeWorkspaceIdRef = useRef(workspaceId);
  const routeWorkspaceIdRef = useRef(workspaceId);
  activeWorkspaceIdRef.current = workspaceId;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (routeWorkspaceIdRef.current !== workspaceId) {
      routeWorkspaceIdRef.current = workspaceId;
      setSelectedTier(suggestedTier);
      setIsChargeNow(phase === 'after_renewal');
      setChargeView({ status: 'confirm' });
      setReactivationPreview({ status: 'idle' });
    }
  }, [workspaceId, suggestedTier, phase]);

  useEffect(() => {
    if (phase === 'after_renewal') setIsChargeNow(true);
  }, [phase]);

  const catalogOptions = useMemo(
    () => ({ frequency, currency }),
    [frequency, currency],
  );

  // ---- Catalog fetch (once per workspace / retry) ----
  useEffect(() => {
    let cancelled = false;
    setCatalog({ status: 'loading' });

    SubscriptionManagerService.getProducts(workspaceId)
      .then((response) => {
        if (cancelled) return;
        const allProducts = [
          ...response.allRelatedProducts,
          ...response.availableForPurchase,
        ];
        setProducts(allProducts);
        setCatalog({
          status: 'ready',
          tierProducts: resolveTierProducts(allProducts, catalogOptions),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setProducts([]);
        setCatalog({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, catalogOptions, catalogNonce]);

  const selectedProduct = useMemo(
    () => findLeadAgentProductForTier(selectedTier, products, catalogOptions),
    [selectedTier, products, catalogOptions],
  );

  // ---- Renewal projection fetch (per tier / retry) ----
  useEffect(() => {
    // No SKU for the selected tier → the workspace cannot subscribe to it;
    // surface as error so confirm stays disabled (never fabricate a breakdown).
    if (!selectedProduct) {
      setProjection({ status: 'error' });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setProjection({ status: 'loading' });

    SubscriptionManagerService.getRenewalProjection(
      workspaceId,
      { tier: selectedTier, nextPaymentAt, origin: origin ?? undefined },
      controller.signal,
    )
      .then((data) => {
        if (controller.signal.aborted) return;
        setProjection({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        // Aborted requests are expected on tier switch — keep the loading
        // state of the newer request, don't flash an error.
        if (isCanceled(error) || controller.signal.aborted) return;
        setProjection({ status: 'error' });
      });

    return () => {
      controller.abort();
    };
  }, [
    workspaceId,
    selectedProduct,
    selectedTier,
    nextPaymentAt,
    origin,
    projectionNonce,
  ]);

  // ---- Immediate reactivation preview (after renewal only) ----
  useEffect(() => {
    if (!isChargeNow) {
      setReactivationPreview((current) =>
        current.status === 'idle' ? current : { status: 'idle' },
      );
      return;
    }
    if (!workspaceId) {
      setReactivationPreview({ status: 'error' });
      return;
    }

    reactivateAbortRef.current?.abort();
    const controller = new AbortController();
    reactivateAbortRef.current = controller;
    setReactivationPreview((current) =>
      current.status === 'loading' ? current : { status: 'loading' },
    );

    LeadAgentAcceptanceService.getReactivatePreview(workspaceId, {
      tier: selectedTier,
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        setReactivationPreview({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (isCanceled(error) || controller.signal.aborted) return;
        setReactivationPreview({ status: 'error' });
      });

    return () => controller.abort();
  }, [isChargeNow, workspaceId, selectedTier, reactivationNonce]);

  const selectTier = useCallback((tier: tLeadAgentSelectableTier) => {
    setSelectedTier(tier);
    setChargeView({ status: 'confirm' });
  }, []);

  const startChargeNow = useCallback(() => {
    setChargeView({ status: 'confirm' });
    setIsChargeNow(true);
  }, []);

  const refreshReactivatePreview = useCallback(() => {
    setChargeView({ status: 'confirm' });
    setReactivationPreview({ status: 'loading' });
    setReactivationNonce((nonce) => nonce + 1);
  }, []);

  const confirmChargeNow =
    useCallback(async (): Promise<tLeadAgentReactivateResult | null> => {
      if (chargeInFlightRef.current || reactivationPreview.status !== 'ready') {
        return null;
      }

      const prorationDate = reactivationPreview.data.prorationDate;
      const tier = selectedTier;
      const requestWorkspaceId = workspaceId;
      chargeInFlightRef.current = true;
      setChargeView({ status: 'processing' });

      try {
        const result = await LeadAgentAcceptanceService.reactivateLeadAgent(
          requestWorkspaceId,
          { tier, prorationDate },
        );
        if (
          !isMountedRef.current ||
          activeWorkspaceIdRef.current !== requestWorkspaceId
        ) {
          return null;
        }

        if (result.status === 'ok') {
          setChargeView({ status: 'success', result });
          return result;
        }
        if (result.status === 'pending') {
          setChargeView({
            status: 'pending',
            kind:
              reactivationPreview.data.collectionMethod === 'send_invoice'
                ? 'invoice'
                : 'open_invoice',
            result,
          });
          return result;
        }
        if (result.status === 'failed') {
          refreshPreviewOnRetryRef.current = true;
          setChargeView({ status: 'error' });
          return null;
        }

        const clientSecret = result.paymentIntentClientSecret;
        const stripe = await stripePromise;
        if (
          !isMountedRef.current ||
          activeWorkspaceIdRef.current !== requestWorkspaceId
        ) {
          return null;
        }
        if (!stripe || !clientSecret) {
          refreshPreviewOnRetryRef.current = false;
          setChargeView({ status: 'error' });
          return null;
        }

        const confirmation = await stripe.confirmCardPayment(clientSecret);
        if (
          !isMountedRef.current ||
          activeWorkspaceIdRef.current !== requestWorkspaceId
        ) {
          return null;
        }
        const { status } = extractPaymentIntentData(confirmation);
        if (confirmation.error || status !== 'succeeded') {
          refreshPreviewOnRetryRef.current = true;
          setChargeView({ status: 'error' });
          return null;
        }

        setChargeView({ status: 'pending', kind: 'webhook', result });
        return result;
      } catch (error) {
        if (
          !isMountedRef.current ||
          activeWorkspaceIdRef.current !== requestWorkspaceId
        ) {
          return null;
        }
        const code = readLeadAgentErrorCode(error);
        if (
          code === 'stale_reactivation_preview' ||
          code === 'renewal_in_progress'
        ) {
          refreshReactivatePreview();
          return null;
        }
        // A timed-out/failed POST or rejected Stripe promise may still finish.
        // Keep the pinned date so retry reuses the backend idempotency key.
        refreshPreviewOnRetryRef.current = false;
        setChargeView({ status: 'error' });
        return null;
      } finally {
        chargeInFlightRef.current = false;
      }
    }, [
      reactivationPreview,
      selectedTier,
      workspaceId,
      refreshReactivatePreview,
    ]);

  const retryChargeNow = useCallback(() => {
    if (chargeInFlightRef.current) return;
    if (
      reactivationPreview.status !== 'ready' ||
      refreshPreviewOnRetryRef.current
    ) {
      refreshPreviewOnRetryRef.current = false;
      refreshReactivatePreview();
      return;
    }
    setChargeView({ status: 'confirm' });
  }, [reactivationPreview.status, refreshReactivatePreview]);

  const retry = useCallback(() => {
    setCatalog((current) =>
      current.status === 'error' ? { status: 'loading' } : current,
    );
    setCatalogNonce((n) => n + 1);
    setProjectionNonce((n) => n + 1);
  }, []);

  return {
    catalog,
    projection,
    selectedTier,
    selectedProduct,
    isChargeNow,
    reactivationPreview,
    chargeView,
    selectTier,
    startChargeNow,
    confirmChargeNow,
    retryChargeNow,
    retry,
  };
}
